import { generateNKeysBetween } from "fractional-indexing";
import type { InkElement, SepiaElement } from "../../../shared/model";
import type { Editor, PointerInfo, Tool, ToolId } from "../editor";
import { bounds, hitTest, inkWorldPoints, newId, round2 } from "../elements";
import { type Vec, boxFromPoints, dist, distToSegment } from "../geometry";

/** Eraser: whole strokes by default, or "precise" to cut strokes where the eraser passes. */
export class EraserTool implements Tool {
  readonly id: ToolId = "eraser";
  private last: Vec | null = null;
  private active = false;
  private erasedAny = false;
  /** a layer the eraser passed over but left alone (it only works on the active layer) */
  private missed: string | null = null;
  private hinted = false;

  constructor(private editor: Editor) {}

  cursor(): string {
    return "none";
  }

  private radius(p: PointerInfo): number {
    const base = this.editor.style.eraserSize;
    // a firmer press with the pencil erases a little wider
    const k = p.hasPressure ? 0.75 + p.pressure * 0.6 : 1;
    return (base * k) / this.editor.camera.z;
  }

  onDown(p: PointerInfo): void {
    this.showCursor(p);
    if (!this.editor.style.eraseAllLayers && !this.editor.canDrawOnActiveLayer()) return;
    this.active = true;
    this.erasedAny = false;
    this.missed = null;
    this.last = p.world;
    this.eraseAlong(p.world, p.world, this.radius(p));
  }

  onMove(p: PointerInfo, coalesced: PointerInfo[]): void {
    this.showCursor(p);
    if (!this.active) return;
    for (const c of coalesced) {
      this.eraseAlong(this.last ?? c.world, c.world, this.radius(c));
      this.last = c.world;
    }
  }

  onUp(): void {
    const ed = this.editor;
    if (this.active && !this.erasedAny && this.missed && !this.hinted) {
      this.hinted = true;
      const other = ed.layerById(this.missed)?.name ?? "another layer";
      const here = ed.layerById(ed.activeLayerId())?.name ?? "this layer";
      ed.host.toast(`That's on “${other}”. The eraser works on the active layer (“${here}”) — switch layers, or pick “all layers”.`);
    }
    this.active = false;
    this.last = null;
    this.missed = null;
    if (this.erasedAny) ed.history.seal();
  }

  onCancel(): void {
    this.onUp();
    this.editor.setOverlay({ eraser: null });
  }

  onHover(p: PointerInfo | null): void {
    if (!p) this.editor.setOverlay({ eraser: null });
    else this.showCursor(p);
  }

  deactivate(): void {
    this.hinted = false;
    this.editor.setOverlay({ eraser: null });
  }

  private showCursor(p: PointerInfo): void {
    this.editor.setOverlay({ eraser: { p: p.world, r: this.radius(p) } });
  }

  private eraseAlong(a: Vec, b: Vec, r: number): void {
    const ed = this.editor;
    const box = { x: Math.min(a.x, b.x) - r, y: Math.min(a.y, b.y) - r, w: Math.abs(a.x - b.x) + 2 * r, h: Math.abs(a.y - b.y) + 2 * r };
    const dels: string[] = [];
    const puts: SepiaElement[] = [];
    const precise = ed.style.eraserMode === "precise";
    const all = ed.doc.all();
    for (let idx = 0; idx < all.length; idx++) {
      const el = all[idx];
      if (el.type !== "ink" && el.type !== "line" && el.type !== "shape") continue;
      // things on other open layers are only hit-tested to explain why they survive
      const erasable = ed.isErasable(el);
      if (!erasable && (this.missed !== null || !ed.isEditable(el))) continue;
      const eb = bounds(el);
      if (eb.x > box.x + box.w || eb.x + eb.w < box.x || eb.y > box.y + box.h || eb.y + eb.h < box.y) continue;
      if (el.type === "ink") {
        const pts = inkWorldPoints(el);
        const reach = r + el.size / 2;
        let hit = false;
        for (let i = 0; i < pts.length && !hit; i++) {
          if (distToSegment(pts[i], a, b) <= reach) hit = true;
          else if (i > 0 && segDist(pts[i - 1], pts[i], a, b) <= reach) hit = true;
        }
        if (!hit) continue;
        if (!erasable) {
          this.missed = ed.layerOf(el);
          continue;
        }
        dels.push(el.id);
        if (precise) {
          const pieces = splitStroke(el, pts, a, b, reach);
          if (pieces.length) {
            // pieces keep the stroke's place in its layer's stacking order
            const after = all[idx + 1];
            const next = after && after.z > el.z && ed.layerOf(after) === ed.layerOf(el) ? after.z : null;
            const keys = pieces.length > 1 ? generateNKeysBetween(el.z, next, pieces.length - 1) : [];
            pieces.forEach((piece, i) => puts.push({ ...piece, z: i === 0 ? el.z : keys[i - 1] }));
          }
        }
      } else {
        // shapes and connectors go whole, and only when the eraser touches their outline
        const samples = Math.max(1, Math.ceil(dist(a, b) / Math.max(r, 1e-3)));
        for (let s = 0; s <= samples; s++) {
          const q = { x: a.x + ((b.x - a.x) * s) / samples, y: a.y + ((b.y - a.y) * s) / samples };
          if (hitTest(el, q, r, true)) {
            if (erasable) dels.push(el.id);
            else this.missed = ed.layerOf(el);
            break;
          }
        }
      }
    }
    if (!dels.length) return;
    this.erasedAny = true;
    ed.commit({ puts, dels }, { key: "erase" });
  }
}

function segDist(p: Vec, q: Vec, a: Vec, b: Vec): number {
  return Math.min(distToSegment(p, a, b), distToSegment(q, a, b), distToSegment(a, p, q), distToSegment(b, p, q));
}

/** Pieces of a stroke that survive an eraser pass (densified so short erasures still cut). */
function splitStroke(el: InkElement, world: Vec[], a: Vec, b: Vec, reach: number): InkElement[] {
  const pres: number[] = [];
  for (let i = 2; i < el.pts.length; i += 3) pres.push(el.pts[i]);
  const dense: { p: Vec; pr: number }[] = [];
  const step = Math.max(reach / 2, 0.5);
  for (let i = 0; i < world.length; i++) {
    if (i > 0) {
      const d = dist(world[i - 1], world[i]);
      const n = Math.floor(d / step);
      for (let k = 1; k < n; k++) {
        const t = k / n;
        dense.push({ p: { x: world[i - 1].x + (world[i].x - world[i - 1].x) * t, y: world[i - 1].y + (world[i].y - world[i - 1].y) * t }, pr: pres[i - 1] + (pres[i] - pres[i - 1]) * t });
      }
    }
    dense.push({ p: world[i], pr: pres[i] });
  }
  const pieces: { p: Vec; pr: number }[][] = [];
  let cur: { p: Vec; pr: number }[] = [];
  for (const d of dense) {
    if (distToSegment(d.p, a, b) <= reach) {
      if (cur.length) pieces.push(cur);
      cur = [];
    } else cur.push(d);
  }
  if (cur.length) pieces.push(cur);
  return pieces
    .filter((piece) => piece.length >= 2)
    .map((piece) => {
      const bb = boxFromPoints(piece.map((d) => d.p));
      const pts: number[] = [];
      for (const d of piece) pts.push(round2(d.p.x - bb.x), round2(d.p.y - bb.y), d.pr);
      return {
        ...el, id: newId(), x: bb.x, y: bb.y, w: Math.max(1, bb.w), h: Math.max(1, bb.h), rot: 0, pts, t: Date.now()
      };
    });
}
